import _ from 'lodash';
import { TREND_COLORS, TREND_NO_COLOR } from '@/trendData/trendData.constants';

/**
 * Keeps track of the number of items using each palette color, and the last-removed color, allowing consistent
 * color assignments across new items and in new worksheets
 */
let colors: {
  useCount: Record<string, number>;
  lastRemoved: string | null;
} = {
  useCount: _.zipObject(TREND_COLORS, _.map(TREND_COLORS, _.constant(0))),
  lastRemoved: null,
};

/** Reset the allocation pool to a clean slate. Called by stateSynchronizer on worksheet rehydration. */
export function resetColors(): void {
  colors = {
    useCount: _.zipObject(TREND_COLORS, _.map(TREND_COLORS, _.constant(0))),
    lastRemoved: null,
  };
}

/**
 * Gets the next available color. Prefers the last-removed color so that removing
 * and re-adding an item (e.g. a capsuleSeries) keeps the same color; otherwise
 * returns the least-used palette color.
 */
export function findNextColor(): string {
  if (colors.lastRemoved && !colors.useCount[colors.lastRemoved]) {
    return colors.lastRemoved;
  }

  let leastUsedColor = TREND_NO_COLOR;
  let minUseCount = Number.MAX_VALUE;
  _.forOwn(colors.useCount, (count, color) => {
    if (count < minUseCount) {
      leastUsedColor = color;
      minUseCount = count;
    }
  });
  return leastUsedColor;
}

/**
 * Resolves a color for a new item: uses `color` if provided, otherwise picks the
 * next available color. Clears `lastRemoved` and increments the use count.
 */
export function getItemColor(color?: string): string {
  const resolved = color || findNextColor();
  colors.lastRemoved = null;
  updateColorUsage(resolved);
  return resolved;
}

/**
 * Increments the use count for a palette color. Custom colors (not in TREND_COLORS)
 * are ignored — they are user-chosen and should not influence `findNextColor`.
 */
export function updateColorUsage(color: string): void {
  if (_.includes(TREND_COLORS, color)) {
    colors.useCount[color] += 1;
  }
}

/**
 * Clears the `lastRemoved` hint without touching use counts. Call this after
 * bulk-removing all items from a store (e.g. during `initialize`) so the next
 * add picks the truly least-used color instead of the last removed one.
 */
export function clearLastRemovedColor(): void {
  colors.lastRemoved = null;
}

/**
 * Decrements the use count and records the color as the last removed, so a
 * re-add of the same item gets the same color back.
 */
export function removeColor(color: string): void {
  if (_.includes(TREND_COLORS, color)) {
    colors.lastRemoved = color;
    const count = colors.useCount[color];
    if (count) {
      colors.useCount[color] = count - 1;
    }
  }
}
